import _ from "lodash";
import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import UIModal from "UIComponents/UIModal";
import Loading from "UIComponents/Loading";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import {
  Button,
  IconButton,
  Dropdown,
  DropdownMenu,
  ButtonDisclosure,
  Modal,
} from "@toddle-edu/ds-web";
import { CloseOutlined, PencilLineOutlined } from "@toddle-edu/ds-icons";
import { getOrganizationPointSetQuery } from "ScoreCategories/modules/ScoreCategoriesQueries";
import { getOrganizationPointSetFromCache } from "ScoreCategories/modules/ScoreCategoriesGraphqlHelpers";
import {
  getGradeScaleTypeOptions,
  getCategoryGradeScaleSet,
} from "ScoreCategories/modules/Utils";
import GradeScale from "GradeScaleSetup/components/GradeScale";
import classNames from "classnames";
import classes from "./CategoryGradeScale.scss";
import { getSettingValue } from "modules/PermissionModule";
import { FINAL_GRADE_CALCULATION_SETTING } from "GradingSetup/modules/Utils";
import { MANUAL } from "Constants/stringConstants";
import { updateScoreCategoryGradeScaleType } from "ScoreCategories/modules/ScoreCategoriesModule";

const styles = {
  modalBody: { padding: "0 24px" },
};

const CategoryGradeScale = ({
  t,
  label,
  isLoading,
  closeModal,
  gradeScaleSet,
  gradeScaleType,
  gradeBoundaryId,
  prevGradeScaleType,
  isViewMode: initialIsViewMode = false,
  curriculumProgramId,
  setIsTemplateUpdated,
  defaultGradeScaleSet,
  onUpdateGradeScaleBoundary,
  isFinalGradeManuallyAssigned,
  overallCategoryGradeBoundaryValues,
  updateOverallCategoryGradeBoundaryValues,
  showEditBtn = false,
  isGradingPeriodUnlocked,
  showUpdatedDesign = false,
  templateFieldsObj,
  shouldUpdateGradeScaleTypeOnSave = false,
  categoryId,
  updateScoreCategoryGradeScaleType,
}) => {
  const gradeScaleRef = useRef(null);

  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);
  const [isViewMode, setIsViewMode] = useState(initialIsViewMode);
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  const gradeScaleOptions = getGradeScaleTypeOptions({ t });
  const defaultGradeScaleType = _.get(_.first(gradeScaleOptions), "key", "");

  const buttonLabel = _.find(
    gradeScaleOptions,
    ({ key }) => key === gradeScaleType
  )?.label;

  const subtextClass = classNames(classes.subtext, "text-body-1");

  useEffect(() => {
    if (!isViewMode && _.isEmpty(gradeScaleType)) {
      onUpdateGradeScaleBoundary({ gradeScaleType: defaultGradeScaleType });
    }
  }, [
    isViewMode,
    gradeScaleType,
    defaultGradeScaleType,
    onUpdateGradeScaleBoundary,
  ]);

  const onClickGradeScale = e => {
    const key = _.get(e, "key");

    if (key === gradeScaleType) return;

    onUpdateGradeScaleBoundary({ gradeScaleType: key });
  };

  const onClickSave = async () => {
    if (!isViewMode && gradeScaleType === "CUSTOM") {
      const isCreateMode = !gradeBoundaryId;
      const response = await gradeScaleRef.current?.saveGradeScale(
        isCreateMode
      );
      const id = _.get(response, "id");
      if (id) {
        updateOverallCategoryGradeBoundaryValues({
          gradeBoundaryId: id,
          categoryGradeBoundaryValues: response,
        });
        onUpdateGradeScaleBoundary({ gradeBoundaryId: id, gradeScaleType });
      }
      if (!isCreateMode) {
        setIsTemplateUpdated?.(true);
      }
    }

    if (shouldUpdateGradeScaleTypeOnSave) {
      setShowFullScreenLoader(true);

      await updateScoreCategoryGradeScaleType({
        categorySetId: _.get(templateFieldsObj, "id"),
        isShowGradeBoundaries: _.get(
          templateFieldsObj,
          "isShowGradeBoundaries"
        ),
        categoryId,
        gradeScaleType,
      });

      setShowFullScreenLoader(false);
    }

    closeModal();
  };

  const onClickCancel = () => {
    if (!isViewMode) {
      onUpdateGradeScaleBoundary({ gradeScaleType: prevGradeScaleType });
    }
    closeModal();
  };

  const categoryGradeScaleSet = getCategoryGradeScaleSet({
    gradeScaleSet,
    gradeScaleType,
    gradeBoundaryId,
    defaultGradeScaleSet,
    overallCategoryGradeBoundaryValues,
  });

  const getTitleLocaleKey = () => {
    return isViewMode
      ? "academicSetup:grade_boundary_for_category_with_label"
      : "academicSetup:add_grade_boundary_for_category_with_label";
  };

  const showEditButton = showEditBtn && isViewMode && isGradingPeriodUnlocked;

  const getHeaderTitle = () => {
    return t(getTitleLocaleKey(), {
      label,
    });
  };

  const getHeaderSubTitle = () => {
    return t(
      "academicSetup:convert_the_overall_score_from_this_category_to_a_grade"
    );
  };

  const getActionElement = () => {
    return showEditButton ? (
      <Button
        onClick={() => setIsViewMode(false)}
        icon={<PencilLineOutlined />}
        variant="neutral"
        type="outlined"
        dsVersion="2.0"
        disabled={isLoading}
      >
        {t("common:edit")}
      </Button>
    ) : null;
  };

  const getModalBody = () => {
    const dropdownLabelClasses = classNames({
      ["text-label-2"]: !showUpdatedDesign,
      ["text-label-l"]: showUpdatedDesign,
    });

    return (
      <>
        <div className={classes.gradeScaleTypeSelectionCon}>
          <div className={dropdownLabelClasses}>
            {t(
              "academicSetup:choose_how_you_would_like_to_define_grade_boundary_for_this_category"
            )}
          </div>
          <Dropdown
            disabled={isViewMode}
            overlay={
              <DropdownMenu
                hasMultilineOptions={true}
                options={gradeScaleOptions}
                onClick={onClickGradeScale}
                dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
              />
            }
            dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
          >
            <ButtonDisclosure
              _legacy_variant={"outlined"}
              variant="neutral"
              type="outlined"
              size={"medium"}
              dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
            >
              {buttonLabel}
            </ButtonDisclosure>
          </Dropdown>
        </div>

        {isLoading ? (
          <div className={classes.loadingContainer}>
            <Loading />
          </div>
        ) : (
          <GradeScale
            t={t}
            isEditMode={true}
            isModalMode={true}
            isCategoryGradeScale={true}
            gradeScaleRef={gradeScaleRef}
            gradeBoundaryId={gradeBoundaryId}
            gradeScaleSet={categoryGradeScaleSet}
            curriculumProgramId={curriculumProgramId}
            setIsSaveButtonDisabled={setIsSaveButtonDisabled}
            isPreviewMode={isViewMode || gradeScaleType === "TERM_GRADE_SCALE"}
            isFinalGradeManuallyAssigned={isFinalGradeManuallyAssigned}
            showUpdatedDesign={showUpdatedDesign}
          />
        )}
      </>
    );
  };

  const getModalFooter = () => {
    return (
      <>
        <Button
          _legacy_size="large"
          size="medium"
          _legacy_variant="outlined-subtle"
          variant="neutral"
          type="outlined"
          onClick={onClickCancel}
          dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
        >
          {isViewMode ? t("common:close") : t("common:cancel")}
        </Button>
        {!isViewMode ? (
          <Button
            _legacy_size="large"
            size="medium"
            variant="progressive"
            onClick={onClickSave}
            disabled={isSaveButtonDisabled}
            dsVersion={showUpdatedDesign ? "2.0" : "1.0"}
          >
            {t("common:save")}
          </Button>
        ) : null}
      </>
    );
  };

  if (showUpdatedDesign) {
    return (
      <>
        <Modal
          shouldCloseOnEsc={true}
          onClose={closeModal}
          dsVersion="2.0"
          width="60vw"
          height="70vh"
        >
          <Modal.Header
            title={getHeaderTitle()}
            subtitle={getHeaderSubTitle()}
            actionElement={getActionElement()}
          />
          <Modal.Body isScrollable={true} style={styles.modalBody}>
            {getModalBody()}
          </Modal.Body>
          <Modal.Footer>
            <div className={classes.footerContainerV2}>{getModalFooter()}</div>
          </Modal.Footer>
        </Modal>

        {showFullScreenLoader ? <FullScreenLoader /> : null}
      </>
    );
  }

  return (
    <UIModal
      isOpen={true}
      modalContent={classes.modalContent}
      overlayContainer={classes.overlayContainer}
    >
      <div className={classes.headerContainer}>
        <div className={classes.titleContainer}>
          <div className="heading-4">{getHeaderTitle()}</div>
          <div className={subtextClass}>{getHeaderSubTitle()}</div>
        </div>
        <div className={classes.headerBtnContainer}>
          {getActionElement()}
          <IconButton
            icon={<CloseOutlined variant="subtle" />}
            _legacy_variant="plain"
            variant="neutral"
            type="plain"
            onClick={onClickCancel}
          />
        </div>
      </div>

      <div className={classes.bodyContainer}>{getModalBody()}</div>

      <div className={classes.footerContainer}>{getModalFooter()}</div>
    </UIModal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const organizationId = _.get(state, "login.userInfo.org_id");

  const {
    index,
    termId,
    subjectId,
    categoryId,
    templateFieldsObj,
    curriculumProgramId,
    portalType,
  } = ownProps;

  const categoryObj = _.get(templateFieldsObj, ["fieldValues", index]);

  const gradeScaleType = _.get(
    categoryObj,
    "gradeScaleType",
    ownProps?.gradeScaleType
  );

  const gradeId = _.get(templateFieldsObj, "courses.0");

  const overallCategoryGradeBoundaryValues = _.get(
    ownProps,
    "overallCategoryGradeBoundaryValues",
    {}
  );

  const associatedEntities = [];

  if (gradeScaleType === "TERM_GRADE_SCALE") {
    associatedEntities.push(
      { ids: termId, type: "ACADEMIC_TERM" },
      { ids: subjectId, type: "SUBJECT" }
    );
    if (gradeId) {
      associatedEntities.push({ ids: gradeId, type: "GRADE" });
    }
  } else {
    associatedEntities.push({
      ids: categoryId,
      type: "ACADEMIC_CRITERIA_VALUE",
    });
  }

  const pointSetVariables = {
    id: organizationId,
    filters: {
      types: "LOCAL_GRADE",
      curriculumProgramIds: curriculumProgramId,
      associatedEntites: associatedEntities,
    },
    portalType,
  };

  const finalGradeCalculationMethod = getSettingValue({
    name: FINAL_GRADE_CALCULATION_SETTING,
    currentCurriculumProgramId: curriculumProgramId,
  });

  const isFinalGradeManuallyAssigned = finalGradeCalculationMethod === MANUAL;

  return {
    isData: true,
    isLoading: false,
    gradeScaleType,
    gradeScaleSet: {},
    pointSetVariables,
    overallCategoryGradeBoundaryValues,
    isFinalGradeManuallyAssigned,
  };
};

const mapActionCreators = {
  updateScoreCategoryGradeScaleType,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getOrganizationPointSetQuery, {
    name: "getOrganizationPointSet",
    skip: ({ categoryId, gradeBoundaryId, gradeScaleType }) =>
      _.isEmpty(gradeScaleType) ||
      (gradeScaleType === "CUSTOM" && (!categoryId || !gradeBoundaryId)),
    options: ({ pointSetVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: pointSetVariables,
    }),
    props({
      getOrganizationPointSet,
      ownProps: { pointSetVariables, isData, isLoading },
    }) {
      const organizationPointSetData = getOrganizationPointSetFromCache({
        variables: pointSetVariables,
      });

      const organizationPointSet = _.get(
        organizationPointSetData,
        "pointAssessmentSets.edges",
        []
      );

      return {
        gradeScaleSet: _.get(_.first(organizationPointSet), "node", {}),
        isData: !_.isEmpty(organizationPointSetData) && isData,
        isLoading:
          _.includes([1, 2], getOrganizationPointSet.networkStatus) ||
          isLoading,
      };
    },
  })
)(CategoryGradeScale);
